import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import {
  ShoppingCart, Star, Utensils, Tag, Package, ChefHat, ImageOff, ArrowLeft,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { fetchFoodById } from "../../services/catalog.api";
import { listFoodReviews } from "../../services/review.api";
import { REVIEW_RATINGS, foodTypeLabel } from "../../services/normalizers";
import { addItem } from "../../Redux/State-slice/CartSlice";
import labels from "../../translationData/currency.json";
import "./food-details.css";

const sym = labels.country === "Bangladesh"
  ? labels.currency.bdt.symbol
  : labels.currency.usa.symbol;

// The API computes the effective price itself; the local fallback only covers
// the moment before the query resolves.
const calcFinalPrice = (food) => {
  if (!food) return 0;
  if (food.discountedPrice != null) return food.discountedPrice;
  const base = food.price || 0;
  const disc = food.discountType === "percentage"
    ? base * ((food.discountValue || 0) / 100)
    : (food.discountValue || 0);
  return Math.max(0, base - disc);
};

const fetchFoodDetails = (id) => fetchFoodById(id);

function Stars({ rating = 0 }) {
  return (
    <div className="fd-stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          fill={i < Math.round(rating) ? "#f59e0b" : "none"}
          color={i < Math.round(rating) ? "#f59e0b" : "#d1d5db"}
        />
      ))}
    </div>
  );
}

/** Reviews left on this specific dish. */
function FoodReviews({ foodId }) {
  const { data: reviews = [], isPending } = useQuery({
    queryKey: ["food-reviews", foodId],
    queryFn: () => listFoodReviews(foodId, { limit: 10 }),
    enabled: !!foodId,
  });

  if (isPending) {
    return (
      <div className="fd-card">
        <Skeleton height={16} width={120} style={{ marginBottom: 10 }} />
        <Skeleton count={2} height={14} />
      </div>
    );
  }

  if (!reviews.length) return null;

  return (
    <div className="fd-card">
      <p style={{
        fontSize: 11, fontWeight: 700, color: "#9ca3af",
        textTransform: "uppercase", letterSpacing: 0.5,
        margin: "0 0 12px", fontFamily: "'Inter', sans-serif",
      }}>
        Reviews
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {reviews.map((review) => {
          const tone = REVIEW_RATINGS.find((r) => r.code === review.rating);
          return (
            <div key={review._id}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{
                  fontSize: 13, fontWeight: 700, color: "#111827",
                  fontFamily: "'Inter', sans-serif",
                }}>
                  {review.reviewerName || "Customer"}
                </span>
                <span style={{
                  padding: "1px 9px", borderRadius: 12,
                  background: tone?.color || "#e5e7eb", color: "#fff",
                  fontSize: 10, fontWeight: 700, fontFamily: "'Inter', sans-serif",
                }}>
                  {review.ratingLabel}
                </span>
              </div>
              {review.content && (
                <p style={{
                  margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  {review.content}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FoodDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const [added, setAdded] = useState(false);

  const { data: food, isPending, error } = useQuery({
    queryKey: ["food-details", id],
    queryFn: () => fetchFoodDetails(id),
    retry: 1,
    staleTime: 60000,
  });

  const handleAddToCart = () => {
    dispatch(addItem(food));
    toast.success("Added to cart!");
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const finalPrice = calcFinalPrice(food);
  const hasDiscount = food?.discountValue > 0;
  const hasImg = food?.image && !imgError;

  if (error) return (
    <div className="home-content-shell page-navbar-space pb-5">
      <div style={{ padding: 16, borderRadius: 12, background: "#fee2e2", color: "#991b1b", fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
        Failed to load food details. Please go back and try again.
      </div>
    </div>
  );

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Back */}
      <Link to="/all-food" style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: 13, fontWeight: 600, color: "#6b7280",
        textDecoration: "none", fontFamily: "'Inter', sans-serif", marginBottom: 20,
      }}>
        <ArrowLeft size={15} /> All Foods
      </Link>

      <div className="fd-layout">

        {/* ── LEFT: image ── */}
        <motion.div
          className="fd-img-wrap"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {isPending ? (
            <Skeleton height="100%" style={{ display: "block", lineHeight: "unset" }} />
          ) : hasImg ? (
            <img
              src={food.image}
              alt={food.name}
              className="fd-img"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="fd-img-placeholder">
              <ImageOff size={40} color="#d1d5db" />
              <span>No image available</span>
            </div>
          )}
        </motion.div>

        {/* ── RIGHT: details ── */}
        <div className="fd-right">

          {/* Name + rating */}
          <div className="fd-card">
            {isPending ? (
              <>
                <Skeleton height={28} width="70%" style={{ marginBottom: 10 }} />
                <Skeleton height={14} width="40%" style={{ marginBottom: 14 }} />
                <Skeleton height={48} width="50%" />
              </>
            ) : (
              <>
                <h1 style={{
                  fontWeight: 800, fontSize: 24, margin: "0 0 6px",
                  color: "#0f172a", fontFamily: "'Inter', sans-serif", lineHeight: 1.3,
                }}>
                  {food?.name}
                </h1>

                {/* Rating comes from the food's own review cache — no reviews
                    means no stars, rather than a flattering default. */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  {food?.reviews?.count > 0 ? (
                    <>
                      <Stars rating={food.reviews.rating} />
                      <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                        {food.reviews.rating} ({food.reviews.count} review
                        {food.reviews.count === 1 ? "" : "s"})
                      </span>
                      {food?.modeRatingLabel && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: "#374151",
                          background: "#f1f5f9", borderRadius: 12, padding: "2px 9px",
                          fontFamily: "'Inter', sans-serif",
                        }}>
                          Mostly “{food.modeRatingLabel}”
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                      No reviews yet
                    </span>
                  )}
                </div>

                {/* Price block */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: 30, fontWeight: 900, color: "#0d6efd",
                    fontFamily: "'Inter', sans-serif",
                  }}>
                    {sym}{finalPrice.toLocaleString()}
                  </span>
                  {hasDiscount && (
                    <span style={{
                      fontSize: 16, fontWeight: 500, color: "#9ca3af",
                      textDecoration: "line-through", fontFamily: "'Inter', sans-serif",
                    }}>
                      {sym}{(food.price || 0).toLocaleString()}
                    </span>
                  )}
                  {hasDiscount && (
                    <span style={{
                      fontSize: 12, fontWeight: 700, color: "#059669",
                      background: "#dcfce7", borderRadius: 20, padding: "3px 10px",
                      fontFamily: "'Inter', sans-serif",
                    }}>
                      {food.discountValue}{food.discountType === "percentage" ? "%" : sym} OFF
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Info rows */}
          <div className="fd-card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {isPending ? <Skeleton count={3} height={16} /> : (
              <>
                {food?.type && (
                  <div className="fd-info-row">
                    <Utensils size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
                    <span className="fd-info-label">Type</span>
                    <span style={{ fontWeight: 600 }}>{foodTypeLabel(food.type)}</span>
                  </div>
                )}
                {food?.quantity != null && (
                  <div className="fd-info-row">
                    <Package size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
                    <span className="fd-info-label">Quantity</span>
                    <span style={{ fontWeight: 600 }}>{food.quantity}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Description */}
          {(isPending || food?.description) && (
            <div className="fd-card">
              <p style={{
                fontSize: 11, fontWeight: 700, color: "#9ca3af",
                textTransform: "uppercase", letterSpacing: 0.5,
                margin: "0 0 8px", fontFamily: "'Inter', sans-serif",
              }}>
                Description
              </p>
              {isPending ? <Skeleton count={3} height={14} /> : (
                <p style={{
                  margin: 0, fontSize: 14, color: "#374151",
                  fontFamily: "'Inter', sans-serif", lineHeight: 1.7,
                }}>
                  {food.description}
                </p>
              )}
            </div>
          )}

          {/* Tags */}
          {(isPending || food?.tags?.length > 0) && (
            <div className="fd-card">
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <Tag size={13} color="#9ca3af" />
                <span style={{
                  fontSize: 11, fontWeight: 700, color: "#9ca3af",
                  textTransform: "uppercase", letterSpacing: 0.5,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  Tags
                </span>
              </div>
              {isPending ? <Skeleton height={28} width="60%" /> : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {food.tags.map((tag, i) => (
                    <Link key={i} to={`/all-food?tag=${tag}`} className="fd-tag">
                      {tag}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Seller */}
          {(isPending || food?.sellerId) && (
            <div className="fd-card">
              <p style={{
                fontSize: 11, fontWeight: 700, color: "#9ca3af",
                textTransform: "uppercase", letterSpacing: 0.5,
                margin: "0 0 12px", fontFamily: "'Inter', sans-serif",
              }}>
                Kitchen
              </p>
              {isPending ? (
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <Skeleton circle width={48} height={48} />
                  <Skeleton height={16} width={120} />
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <img
                    src={food.sellerId?.kitchenLogo || food.sellerId?.avatar || "/Assets/Img/kitchen_default.png"}
                    alt={food.sellerId?.name}
                    style={{
                      width: 48, height: 48, borderRadius: "50%",
                      objectFit: "cover", border: "2px solid #f1f5f9", flexShrink: 0,
                    }}
                    onError={e => { e.target.src = "/Assets/Img/kitchen_default.png"; }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <p style={{
                      margin: 0, fontSize: 14, fontWeight: 700,
                      color: "#111827", fontFamily: "'Inter', sans-serif",
                    }}>
                      {food.sellerId?.name || food.sellerId?.kitchenName}
                    </p>
                    <Link
                      to={`/SellerProfile/${food.sellerId?._id}`}
                      style={{
                        fontSize: 12, color: "#0d6efd", textDecoration: "none",
                        fontFamily: "'Inter', sans-serif", fontWeight: 500,
                        display: "inline-flex", alignItems: "center", gap: 3, marginTop: 2,
                      }}
                    >
                      <ChefHat size={11} /> View Kitchen
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reviews for this dish */}
          {!isPending && <FoodReviews foodId={id} />}

          {/* Add to cart */}
          <button
            className="fd-cart-btn"
            onClick={handleAddToCart}
            disabled={isPending || added}
            style={{ opacity: isPending ? 0.5 : 1 }}
          >
            <ShoppingCart size={18} />
            {added ? "Added!" : "Add to Cart"}
          </button>

        </div>
      </div>
    </div>
  );
}
